import { Link } from "react-router-dom";
import { ArrowRight, LogIn, ExternalLink, MapPin } from "lucide-react";
import { Button } from "../ui/button";
import ProjectGallery from "../ProjectGallery";

const PLOT_IMG = "https://images.unsplash.com/photo-1560198927-b31ef30c5867?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxmYXJtbGFuZCUyMGFlcmlhbCUyMHZpZXd8ZW58MHx8fHwxNzg2NjE3MjczfDA&ixlib=rb-4.1.0&q=85";

const mapEmbed = (p) => `https://www.google.com/maps?q=${encodeURIComponent(p.map_query || p.location)}&output=embed`;
const mapLink = (p) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(p.map_query || p.location)}`;

function EstateCard({ p, onEnquire }) {
  return (
    <div
      className="border rounded-lg overflow-hidden bg-background flex flex-col transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-0.5 hover:shadow-sm"
      style={{ borderColor: "hsl(var(--border))", ["--accent"]: p.accent_color || "hsl(var(--primary))" }}
      onMouseEnter={(e) => { if (p.accent_color) e.currentTarget.style.borderColor = p.accent_color; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "hsl(var(--border))"; }}
      data-testid={`project-card-${p.id}`}
    >
      {p.accent_color && <div className="h-1" style={{ background: p.accent_color }} />}
      <ProjectGallery
        images={p.gallery && p.gallery.length ? p.gallery : [p.cover_image || PLOT_IMG]}
        alt={p.name}
        testid={`gallery-${p.id}`}
      />
      <div className="p-5 flex-1 flex flex-col">
        <div className="heading text-xl font-semibold">{p.name}</div>
        <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><MapPin size={12} /> {p.location}</div>
        {p.description && <p className="text-sm text-muted-foreground mt-3 line-clamp-3 leading-relaxed">{p.description}</p>}
        <div className="flex items-center gap-3 mt-4 text-[10px] uppercase tracking-widest text-muted-foreground">
          {p.accent_color && <span className="h-2.5 w-2.5 rounded-full inline-block" style={{ background: p.accent_color }} data-testid={`accent-${p.id}`} />}
          <span>{p.plot_count} plots</span>
          {p.available_plots > 0 && <span className="text-primary font-semibold">· {p.available_plots} available</span>}
          {p.default_nightly_rate > 0 && <span>· ₹{Math.round(p.default_nightly_rate).toLocaleString("en-IN")}/night</span>}
        </div>

        {(p.map_query || p.location) && (
          <div className="mt-4 rounded-md overflow-hidden border border-border">
            <iframe
              title={`${p.name} map`}
              src={mapEmbed(p)}
              className="w-full h-40"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid={`map-${p.id}`}
            />
            <a href={mapLink(p)} target="_blank" rel="noreferrer"
               className="block text-[11px] uppercase tracking-widest text-primary font-semibold px-3 py-2 border-t border-border hover:bg-secondary/40 transition-[background-color] duration-200"
               data-testid={`map-link-${p.id}`}>
              Open in Google Maps <ExternalLink size={11} className="inline ml-1" />
            </a>
          </div>
        )}

        <div className="mt-5 pt-4 border-t border-border flex items-center justify-between gap-2">
          <Link to={`/login?project=${p.id}`}>
            <Button size="sm" data-testid={`project-signin-${p.id}`}>
              <LogIn size={14} className="mr-2" /> Sign in
            </Button>
          </Link>
          <Button size="sm" variant="outline" onClick={() => onEnquire(p)} data-testid={`project-enquire-${p.id}`}>
            Enquire
          </Button>
        </div>
      </div>
    </div>
  );
}

export function EstateGrid({ projects, onEnquire }) {
  return (
    <section id="projects" className="border-t border-border bg-card">
      <div className="max-w-6xl mx-auto px-6 py-16 md:py-20">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-3">Our projects</div>
            <h2 className="heading text-3xl md:text-4xl font-semibold">Sign in to your project.</h2>
            <p className="text-sm text-muted-foreground mt-2 max-w-xl">Every project has its own dashboard for owners, field crews and managers. Pick yours and sign in.</p>
          </div>
          <Link to="/login"><Button variant="ghost" data-testid="all-signin-btn">General sign in <ArrowRight size={14} className="ml-1" /></Button></Link>
        </div>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8" data-testid="projects-grid">
          {projects.length === 0 && (
            <div className="col-span-full border border-dashed border-border rounded-lg p-10 text-center text-sm text-muted-foreground" data-testid="projects-empty">
              No projects yet. Once your manager adds one, it will show up here.
            </div>
          )}
          {projects.map((p) => <EstateCard key={p.id} p={p} onEnquire={onEnquire} />)}
        </div>
      </div>
    </section>
  );
}
